import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/styles/tailwind-utils';

// ─── Constants ────────────────────────────────────────────────────────────────

const ITEM_H = 40;            // px per row
const VISIBLE = 5;            // rows visible per column
const PAD = 2;                // rows above/below the selected row
const DRAG_SENSITIVITY = 2.5; // px-per-item divisor multiplier (higher = slower drag)

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_MIN = 1920;

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function parseValue(v: string) {
  if (!v) return { year: null as number | null, month: null as number | null, day: null as number | null };
  const [y, m, d] = v.split('-').map(Number);
  return {
    year: y && y >= YEAR_MIN && y <= CURRENT_YEAR ? y : null,
    month: m && m >= 1 && m <= 12 ? m : null,
    day: d && d >= 1 && d <= 31 ? d : null,
  };
}

function toIso(year: number | null, month: number | null, day: number | null): string {
  if (!year || !month || !day) return '';
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

const YEARS = Array.from({ length: CURRENT_YEAR - YEAR_MIN + 1 }, (_, i) => ({
  value: YEAR_MIN + i,
  label: String(YEAR_MIN + i),
}));

const DAYS = Array.from({ length: 31 }, (_, i) => ({
  value: i + 1,
  label: pad2(i + 1),
}));

// ─── DrumScroller ─────────────────────────────────────────────────────────────
// Handles interaction for a single column. No visual decorations (band/fades)
// — those are rendered once in the parent so they span all three columns.

interface DrumScrollerProps {
  items: { value: number; label: string }[];
  selected: number;
  onSelect: (v: number) => void;
}

function DrumScroller({ items, selected, onSelect }: DrumScrollerProps) {
  const elRef = useRef<HTMLDivElement>(null);
  const [drift, setDrift] = useState(0); // live pixel offset during drag

  // Keep a ref so wheel handler closure always sees the current index
  const idx = Math.max(0, items.findIndex((it) => it.value === selected));
  const idxRef = useRef(idx);
  useEffect(() => { idxRef.current = idx; }, [idx]);

  // Non-passive wheel so we can call preventDefault and keep the page still
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    function handler(e: WheelEvent) {
      e.preventDefault();
      const next = Math.max(0, Math.min(items.length - 1, idxRef.current + Math.sign(e.deltaY)));
      onSelect(items[next]!.value);
    }
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [items, onSelect]);

  // Drag via window-level listeners (avoids pointer-capture + Radix issues)
  function handleMouseDown(e: React.MouseEvent) {
    e.preventDefault(); // prevent text selection
    const el = elRef.current;
    if (!el) return;

    const startY = e.clientY;
    const baseIdx = idxRef.current;

    function onMove(ev: MouseEvent) {
      setDrift((startY - ev.clientY) / DRAG_SENSITIVITY);
    }

    function onUp(ev: MouseEvent) {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);

      const rawDy = startY - ev.clientY;
      let next: number;
      if (Math.abs(rawDy) < 5) {
        // Tap — find which row the user pressed
        const rect = el.getBoundingClientRect();
        const row = Math.floor((ev.clientY - rect.top) / ITEM_H);
        next = Math.max(0, Math.min(items.length - 1, baseIdx + row - PAD));
      } else {
        // Drag — snap to nearest (damped by DRAG_SENSITIVITY)
        const scaledDy = rawDy / DRAG_SENSITIVITY;
        next = Math.max(0, Math.min(items.length - 1, Math.round(baseIdx + scaledDy / ITEM_H)));
      }
      setDrift(0);
      onSelect(items[next]!.value);
    }

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowUp') { e.preventDefault(); onSelect(items[Math.max(0, idx - 1)]!.value); }
    if (e.key === 'ArrowDown') { e.preventDefault(); onSelect(items[Math.min(items.length - 1, idx + 1)]!.value); }
  }

  // translateY shifts the list so items[idx] sits at the center row
  const translateY = (PAD - idx) * ITEM_H - drift;

  return (
    <div
      ref={elRef}
      tabIndex={0}
      role="listbox"
      onMouseDown={handleMouseDown}
      onKeyDown={handleKeyDown}
      className="relative flex-1 cursor-grab select-none overflow-hidden focus-visible:outline-none active:cursor-grabbing"
      style={{ height: VISIBLE * ITEM_H, zIndex: 2 }}
    >
      <div
        style={{
          transform: `translateY(${translateY}px)`,
          // Transition only when not actively dragging (drift === 0 means snapping)
          transition: drift !== 0 ? 'none' : 'transform 200ms cubic-bezier(0.33, 1, 0.68, 1)',
          willChange: 'transform',
        }}
      >
        {items.map((item, i) => {
          const dist = Math.abs(i - idx);
          return (
            <div
              key={item.value}
              role="option"
              aria-selected={i === idx}
              style={{ height: ITEM_H }}
              className={cn(
                'flex items-center justify-center',
                i === idx
                  ? 'text-[15px] font-semibold text-foreground'
                  : dist === 1
                  ? 'text-sm text-muted-foreground/55'
                  : 'text-sm text-muted-foreground/25',
              )}
            >
              {item.label}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── DateOfBirthPicker ────────────────────────────────────────────────────────

export interface DateOfBirthPickerProps {
  value: string; // YYYY-MM-DD or ''
  onChange: (value: string) => void;
  label?: string;
  error?: string;
}

export function DateOfBirthPicker({ value, onChange, label, error }: DateOfBirthPickerProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const parsed = parseValue(value);
  const [tempYear, setTempYear] = useState(parsed.year ?? CURRENT_YEAR - 30);
  const [tempMonth, setTempMonth] = useState(parsed.month ?? 6);
  const [tempDay, setTempDay] = useState(parsed.day ?? 1);

  const [rawYear, setRawYear] = useState(parsed.year ? String(parsed.year) : '');
  const [rawMonth, setRawMonth] = useState(parsed.month ? pad2(parsed.month) : '');
  const [rawDay, setRawDay] = useState(parsed.day ? pad2(parsed.day) : '');

  const yearRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const dayRef = useRef<HTMLInputElement>(null);

  const MONTHS = useMemo(() => {
    const locale = i18n.language.startsWith('ro') ? 'ro' : 'en';
    return Array.from({ length: 12 }, (_, i) => ({
      value: i + 1,
      label: new Intl.DateTimeFormat(locale, { month: 'short' }).format(new Date(2000, i, 1)),
    }));
  }, [i18n.language]);

  // Sync text inputs whenever the external value changes (e.g. form reset)
  useEffect(() => {
    const p = parseValue(value);
    setRawYear(p.year ? String(p.year) : '');
    setRawMonth(p.month ? pad2(p.month) : '');
    setRawDay(p.day ? pad2(p.day) : '');
  }, [value]);

  // Close picker on Escape or click outside the wrapper
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false); }
    function onDown(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  function openPicker() {
    const p = parseValue(value);
    setTempYear(p.year ?? CURRENT_YEAR - 30);
    setTempMonth(p.month ?? 6);
    setTempDay(p.day ?? 1);
    setOpen(true);
  }

  function confirmPicker() {
    onChange(toIso(tempYear, tempMonth, tempDay));
    setOpen(false);
  }

  function flushInputs() {
    const y = parseInt(rawYear, 10);
    const m = parseInt(rawMonth, 10);
    const d = parseInt(rawDay, 10);
    onChange(toIso(
      !isNaN(y) && y >= YEAR_MIN && y <= CURRENT_YEAR ? y : null,
      !isNaN(m) && m >= 1 && m <= 12 ? m : null,
      !isNaN(d) && d >= 1 && d <= 31 ? d : null,
    ));
  }

  function handleYearChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value.replace(/\D/g, '').slice(0, 4);
    setRawYear(v);
    if (v.length === 4) { monthRef.current?.focus(); monthRef.current?.select(); }
  }

  function handleMonthChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value.replace(/\D/g, '').slice(0, 2);
    setRawMonth(v);
    if (v.length === 2) { dayRef.current?.focus(); dayRef.current?.select(); }
  }

  function handleDayChange(e: React.ChangeEvent<HTMLInputElement>) {
    setRawDay(e.target.value.replace(/\D/g, '').slice(0, 2));
  }

  const seg = cn(
    'h-10 rounded-sm border border-input bg-surface px-1 text-center text-sm tabular-nums',
    'placeholder:text-muted-foreground/40',
    'focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1',
    error && 'border-destructive focus:ring-destructive',
  );

  const COLUMN_H = VISIBLE * ITEM_H;
  const FADE_H = PAD * ITEM_H;

  return (
    // position:relative so the picker panel can be absolute-positioned below
    <div ref={wrapperRef} className="relative flex flex-col gap-1">
      {label && <label className="text-sm font-medium text-foreground">{label}</label>}

      <div className="flex items-center gap-1">
        {/* Year */}
        <input
          ref={yearRef} value={rawYear} onChange={handleYearChange} onBlur={flushInputs}
          placeholder="YYYY" inputMode="numeric" aria-label={t('clients.addClientModal.dobYear')}
          className={cn(seg, 'w-[4.5rem]')}
        />
        <span className="select-none text-xs text-muted-foreground/40">/</span>
        {/* Month */}
        <input
          ref={monthRef} value={rawMonth} onChange={handleMonthChange} onBlur={flushInputs}
          placeholder="MM" inputMode="numeric" aria-label={t('clients.addClientModal.dobMonth')}
          className={cn(seg, 'w-11')}
        />
        <span className="select-none text-xs text-muted-foreground/40">/</span>
        {/* Day */}
        <input
          ref={dayRef} value={rawDay} onChange={handleDayChange} onBlur={flushInputs}
          placeholder="DD" inputMode="numeric" aria-label={t('clients.addClientModal.dobDay')}
          className={cn(seg, 'w-11')}
        />
        {/* Calendar toggle */}
        <button
          type="button"
          onClick={() => (open ? setOpen(false) : openPicker())}
          aria-label={t('clients.addClientModal.dobPickerTitle')}
          className={cn(
            'ml-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-sm border border-input bg-surface',
            'text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
            open && 'border-brand/40 bg-brand-subtle text-brand',
            error && 'border-destructive',
          )}
        >
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="1.5" y="2.5" width="13" height="12" rx="1.5" />
            <path d="M5 1v3M11 1v3M1.5 6.5h13" />
          </svg>
        </button>
      </div>

      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}

      {/* ── Picker panel ─────────────────────────────────────────────────────
          Rendered INSIDE the Radix Dialog DOM (no portal) so Radix never
          detects interactions as "outside" the dialog and closes the modal.
      ─────────────────────────────────────────────────────────────────────── */}
      {open && (
        <div className="absolute left-0 top-full z-[100] mt-1 w-72 overflow-visible rounded-2xl border border-border/60 bg-white shadow-xl">

          {/* Title + close */}
          <div className="flex items-center justify-between px-5 pb-2 pt-4">
            <span className="text-[11px] font-semibold uppercase tracking-[0.13em] text-foreground">
              {t('clients.addClientModal.dobPickerTitle')}
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="flex h-7 w-7 items-center justify-center rounded-full text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              ✕
            </button>
          </div>

          {/* Column header labels */}
          <div className="flex px-3 pb-1">
            {[
              t('clients.addClientModal.dobYear'),
              t('clients.addClientModal.dobMonth'),
              t('clients.addClientModal.dobDay'),
            ].map((h) => (
              <div
                key={h}
                className="flex-1 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60"
              >
                {h}
              </div>
            ))}
          </div>

          {/* Drum-roll area — single highlight band + fades span all three columns */}
          <div className="relative mx-3 overflow-hidden rounded-xl" style={{ height: COLUMN_H }}>

            {/* Highlight band — behind the scrollers (z-1) */}
            <div
              className="pointer-events-none absolute inset-x-0 rounded-xl bg-muted/60"
              style={{ top: PAD * ITEM_H, height: ITEM_H, zIndex: 1 }}
            />

            {/* Fade top — above the scrollers (z-3) */}
            <div
              className="pointer-events-none absolute inset-x-0 top-0"
              style={{
                height: FADE_H,
                background: 'linear-gradient(to bottom, white 25%, transparent)',
                zIndex: 3,
              }}
            />

            {/* Fade bottom — above the scrollers (z-3) */}
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0"
              style={{
                height: FADE_H,
                background: 'linear-gradient(to top, white 25%, transparent)',
                zIndex: 3,
              }}
            />

            {/* Three columns — z-2 (above highlight band, below fades) */}
            <div className="flex h-full">
              <DrumScroller items={YEARS} selected={tempYear} onSelect={setTempYear} />
              <DrumScroller items={MONTHS} selected={tempMonth} onSelect={setTempMonth} />
              <DrumScroller items={DAYS} selected={tempDay} onSelect={setTempDay} />
            </div>
          </div>

          {/* Apply */}
          <div className="px-4 pb-5 pt-3">
            <button
              type="button"
              onClick={confirmPicker}
              className="h-12 w-full rounded-xl bg-brand text-[11px] font-semibold uppercase tracking-[0.13em] text-primary-foreground transition-colors hover:bg-brand-hover active:bg-brand-active"
            >
              {t('clients.addClientModal.dobApply')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
